/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */
import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Box, IconButton, Tooltip, Typography, FormHelperText } from '@mui/material';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListOrderedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalSplit';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import TitleIcon from '@mui/icons-material/Title';
import InlineLinkInput from './InlineLinkInput';
import InlineImageInput from './InlineImageInput';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: 13,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
        fontSize: 8,
    },
}));

export default function RichTextEditor({ value, onChange, label, error, helperText }) {
    const [showLinkInput, setShowLinkInput] = React.useState(false);
    const [showImageInput, setShowImageInput] = React.useState(false);
    const [linkInitialValues, setLinkInitialValues] = React.useState({
        url: '',
        text: '',
        isButton: false,
    });

    // Add a state to trigger re-render on selection change
    const [, setSelectionVersion] = React.useState(0);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: {
                    openOnClick: false,
                    HTMLAttributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                    },
                },
            }),
            Image.configure({
                HTMLAttributes: {
                    style: 'width: 100%; display: block;',
                },
            }),
        ],
        content: value, // value should be JSON
        onUpdate: ({ editor }) => {
            onChange(editor.getJSON()); // send JSON to parent
        },
    });

    // Listen for selection changes and force re-render
    useEffect(() => {
        if (!editor) return;
        const handler = () => setSelectionVersion((v) => v + 1);
        editor.on('selectionUpdate', handler);
        return () => {
            editor.off('selectionUpdate', handler);
        };
    }, [editor]);

    useEffect(() => {
        if (editor && value && JSON.stringify(value) !== JSON.stringify(editor.getJSON())) {
            editor.commands.setContent(value, { emitUpdate: true }); // set JSON content
        }
        // eslint-disable-next-line
    }, [value]);

    const selectionRef = useRef({ from: 0, to: 0 });

    // Open inline link input with selected text/link as initial value
    const openLinkInput = () => {
        if (!editor) return;

        // Hide image input if it's open
        setShowImageInput(false);

        const { state } = editor;
        const { from, to, empty } = state.selection;
        let linkMark = editor.getAttributes('link');
        let linkRange = null;
        let selectedText = editor.state.doc.textBetween(from, to, ' ');

        // If selection is empty, check if cursor is inside a link
        if (empty && linkMark?.href) {
            // Find the link mark range at the cursor
            state.doc.nodesBetween(from, from, (node, pos) => {
                if (node.marks) {
                    const found = node.marks.find((mark) => mark.type.name === 'link');
                    if (found) {
                        const start = pos;
                        const end = pos + node.nodeSize;
                        linkRange = { from: start, to: end };
                        selectedText = node.text || '';
                        linkMark = found.attrs;
                    }
                }
            });
        }

        // Save the range for later use in handleInsert
        selectionRef.current = linkRange || { from, to };

        // Set initial values and show the inline input
        setLinkInitialValues({
            url: linkMark?.href || '',
            text: selectedText || '',
            isButton: linkMark?.class === 'button',
        });
        setShowLinkInput(true);
    };

    const openImageInput = () => {
        if (!editor) return;

        // Hide link input if it's open
        setShowLinkInput(false);

        // Save current selection for image insertion
        const { from, to } = editor.state.selection;
        selectionRef.current = { from, to };

        setShowImageInput(true);
    };

    // Handler for inserting link
    const handleLinkInsert = ({ url, text, isButton }) => {
        if (!editor || !url || !text) return;

        const { from, to } = selectionRef.current;
        const linkAttrs = {
            href: url,
            target: '_blank',
            rel: 'noopener noreferrer',
            class: isButton ? 'button' : null,
        };

        editor
            .chain()
            .focus()
            .setTextSelection({ from, to })
            .deleteRange({ from, to })
            .insertContent(text)
            .setTextSelection({ from, to: from + text.length })
            .setLink(linkAttrs)
            .run();

        // Hide the input after inserting
        setShowLinkInput(false);
    };

    // Handler for inserting image
    const handleImageInsert = ({ url }) => {
        if (!editor || !url) return;

        editor.chain().focus().setImage({ src: url }).run();

        // Hide the input after inserting
        setShowImageInput(false);
    };

    // Handler for canceling inputs
    const handleCancel = () => {
        setShowLinkInput(false);
        setShowImageInput(false);
    };

    // Common active style for all buttons
    const activeSx = {
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': { backgroundColor: 'primary.dark' },
    };

    // Helper to check if selection is inside a blockquote
    function isSelectionInBlockquote(editor: Editor | null): boolean {
        if (!editor) return false;
        const { $from } = editor.state.selection;
        for (let d = $from.depth; d > 0; d--) {
            if ($from.node(d).type.name === 'blockquote') return true;
        }
        return false;
    }

    if (!editor) return null;

    return (
        <Box sx={{ width: '100%', textAlign: 'left' }}>
            {label && (
                <Typography
                    variant="body1"
                    sx={{
                        color: error ? 'error.main' : 'text.primary',
                        fontWeight: 500,
                        fontSize: '1rem',
                        mb: 1,
                    }}
                >
                    {label}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    border: '1px solid #c4c4c4',
                    borderBottom: 'none',
                    gap: 1,
                    m: 0,
                    p: 0.5,
                    alignItems: 'center',
                }}
            >
                <Tooltip title="H1">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}
                        sx={editor.isActive('heading', { level: 1 }) ? activeSx : {}}
                    >
                        <StyledBadge badgeContent="H1" color="secondary">
                            <TitleIcon />
                        </StyledBadge>
                    </IconButton>
                </Tooltip>
                <Tooltip title="H2">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
                        sx={editor.isActive('heading', { level: 2 }) ? activeSx : {}}
                    >
                        <StyledBadge badgeContent="H2" color="secondary">
                            <TitleIcon />
                        </StyledBadge>
                    </IconButton>
                </Tooltip>
                <Tooltip title="H3">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
                        sx={editor.isActive('heading', { level: 3 }) ? activeSx : {}}
                    >
                        <StyledBadge badgeContent="H3" color="secondary">
                            <TitleIcon />
                        </StyledBadge>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Bold">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        color={editor.isActive('bold') ? 'primary' : 'default'}
                        sx={editor.isActive('bold') ? activeSx : {}}
                    >
                        <FormatBoldIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        color={editor.isActive('italic') ? 'primary' : 'default'}
                        sx={editor.isActive('italic') ? activeSx : {}}
                    >
                        <FormatItalicIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Link">
                    <IconButton
                        size="small"
                        onClick={openLinkInput}
                        color={editor.isActive('link') || showLinkInput ? 'primary' : 'default'}
                        sx={editor.isActive('link') || showLinkInput ? activeSx : {}}
                    >
                        <LinkIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Image">
                    <IconButton
                        size="small"
                        onClick={openImageInput}
                        color={editor.isActive('image') || showImageInput ? 'primary' : 'default'}
                        sx={editor.isActive('image') || showImageInput ? activeSx : {}}
                    >
                        <ImageIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Insert horizontal rule">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                        color={editor.isActive('horizontalRule') ? 'primary' : 'default'}
                        sx={editor.isActive('horizontalRule') ? activeSx : {}}
                    >
                        <HorizontalRuleIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Blockquote">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        color={isSelectionInBlockquote(editor) ? 'primary' : 'default'}
                        sx={isSelectionInBlockquote(editor) ? activeSx : {}}
                    >
                        <FormatQuoteIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Toggle ordered list">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        color={
                            editor.isActive('orderedList') ||
                            editor.isActive('listItem', { parent: { type: 'orderedList' } })
                                ? 'primary'
                                : 'default'
                        }
                        sx={
                            editor.isActive('orderedList') ||
                            editor.isActive('listItem', { parent: { type: 'orderedList' } })
                                ? activeSx
                                : {}
                        }
                    >
                        <FormatListOrderedIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Toggle unordered list">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        color={
                            editor.isActive('bulletList') ||
                            editor.isActive('listItem', { parent: { type: 'bulletList' } })
                                ? 'primary'
                                : 'default'
                        }
                        sx={
                            editor.isActive('bulletList') ||
                            editor.isActive('listItem', { parent: { type: 'bulletList' } })
                                ? activeSx
                                : {}
                        }
                    >
                        <FormatListBulletedIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Inline Link Input */}
            {showLinkInput && (
                <InlineLinkInput
                    onInsert={handleLinkInsert}
                    onCancel={handleCancel}
                    initialValues={linkInitialValues}
                />
            )}

            {/* Inline Image Input */}
            {showImageInput && (
                <InlineImageInput onInsert={handleImageInsert} onCancel={handleCancel} />
            )}

            <Box
                sx={{
                    border: error ? '1.5px solid' : '1px solid',
                    borderColor: error ? 'error.main' : '#c4c4c4',
                    px: 1,
                    py: 1,
                    background: 'white',
                    width: '100%',
                    '&:focus-within': {
                        borderColor: '#1976d2',
                    },
                    transition: 'border-color 0.2s',
                }}
            >
                <EditorContent editor={editor} className="tiptap" />
            </Box>
            {(helperText || error) && (
                <FormHelperText error={!!error} sx={{ ml: 0 }}>
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
}
